import { AggregateBySectionHeader } from '@monorepo/shared/componentsV2/aggregation/AggregateBySectionHeader';
import { SelectField } from '@monorepo/shared/componentsV2/fields/SelectField';
import {
  allowedDateTransformationsForAggregation,
  compareDatetimeOriginTransformations,
  DatasetPropertyResponse,
  DatasetPropertyType,
  DatasetResponse,
  datetimeTransformationLabels,
  isIntervalAllowedForAggregationStep,
} from 'mapistry-shared';
import React, { useMemo } from 'react';
import styled from 'styled-components';

const StyledSelectField = styled(SelectField)`
  width: 15rem;
`;

export const ALL_TIME_VALUE = '__ALL_TIME_VALUE__';

type DatasetPropertyWithDateTransformation = Omit<
  DatasetPropertyResponse,
  'dateTransformation'
> & {
  dateTransformation: NonNullable<
    DatasetPropertyResponse['dateTransformation']
  >;
};

interface AggregationIntervalProps {
  stepInputDataset: DatasetResponse;
}

function getTimeSeriesRelatedDateRangeProperties(dataset: DatasetResponse) {
  const timeSeriesProperty = dataset.schemaProperties.find(
    (p) => p.isTimeSeriesDate,
  );
  if (!timeSeriesProperty) return [];

  // We will pick child date ranges of time series property as options for aggregation interval,
  // unless time series property is a child date range itself, in this case we need to pick its siblings.
  const timeSeriesDateRangesParentId =
    timeSeriesProperty.type === DatasetPropertyType.DATE_RANGE &&
    timeSeriesProperty.parentPropertyId
      ? timeSeriesProperty.parentPropertyId
      : timeSeriesProperty.id;

  return dataset.schemaProperties.filter(
    (p): p is DatasetPropertyWithDateTransformation =>
      p.parentPropertyId === timeSeriesDateRangesParentId &&
      p.type === DatasetPropertyType.DATE_RANGE &&
      !!p.dateTransformation,
  );
}

const ALL_TIME_OPTION = {
  label: 'All time',
  value: ALL_TIME_VALUE,
};

export function AggregationInterval({
  stepInputDataset,
}: AggregationIntervalProps) {
  const { availableIntervalOptions, areIntervalsHiddenDueToPreviousJoin } =
    useMemo(() => {
      const timeSeriesRelatedProperties =
        getTimeSeriesRelatedDateRangeProperties(stepInputDataset);

      const filteredByAllowedIntervals = timeSeriesRelatedProperties.filter(
        (p) =>
          allowedDateTransformationsForAggregation.includes(
            p.dateTransformation,
          ),
      );

      const dateRangeIntervalOptions = filteredByAllowedIntervals
        // this filtering is separate because we need to see if there are any options that were filtered out
        // due to the largest joined range
        .filter((p) =>
          isIntervalAllowedForAggregationStep({
            aggregationInterval: p.dateTransformation,
            inputLargestJoinedRange: stepInputDataset.largestJoinedRange,
          }),
        )
        .sort(
          (p1, p2) =>
            // Sorting from the biggest interval, e.g. year, to the smallest, e.g. minute
            -1 *
            compareDatetimeOriginTransformations(
              p1.dateTransformation,
              p2.dateTransformation,
            ),
        )
        .map((p) => ({
          label: datetimeTransformationLabels[p.dateTransformation],
          value: p.id,
        }));

      const areAnyIntervalsHiddenDueToPreviousJoin =
        dateRangeIntervalOptions.length < filteredByAllowedIntervals.length;

      return {
        availableIntervalOptions: [
          ALL_TIME_OPTION,
          ...dateRangeIntervalOptions,
        ],
        areIntervalsHiddenDueToPreviousJoin:
          areAnyIntervalsHiddenDueToPreviousJoin,
      };
    }, [stepInputDataset]);

  const tooltipInfo = areIntervalsHiddenDueToPreviousJoin
    ? `The input data source is the result of data connected on ${stepInputDataset.largestJoinedRange} fields, so aggregating on time intervals smaller than ${stepInputDataset.largestJoinedRange} is not allowed.`
    : undefined;

  return (
    <>
      <AggregateBySectionHeader tooltipInfo={tooltipInfo} />
      <StyledSelectField
        fullWidth={false}
        isLabelHidden
        label="Aggregation Period"
        name="intervalPropertyId"
        options={availableIntervalOptions}
      />
    </>
  );
}
